import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { apiUrl } from '../lib/apiRoot';
import {
  loginErrorMessage,
  readAuthError,
  restoreErrorMessage,
  shouldIgnoreRestore,
  writeAuthError,
} from '../lib/authSession';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('ew_token'));
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState(readAuthError);
  const restoreGen = useRef(0);

  const setAuthHeader = useCallback((t) => {
    if (t) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, []);

  const setSessionMessage = useCallback((message) => {
    const text = typeof message === 'string' ? message : '';
    writeAuthError(text);
    setSessionError(text);
  }, []);

  const logout = useCallback((message) => {
    localStorage.removeItem('ew_token');
    setToken(null);
    setUser(null);
    setAuthHeader(null);
    // Only a real string is a message. A sign-out button passes its click
    // event here, which used to show "[object Object]" on /login.
    if (typeof message === 'string' && message) setSessionMessage(message);
    else setSessionMessage('');
  }, [setAuthHeader, setSessionMessage]);

  // Sign out everywhere: this portal's token, then the editor's session on
  // my.<brand> (its cookies are on that host, so only a visit there can clear
  // them). The editor sends the browser back to our sign-in page.
  // The one sign-out every portal control uses. Ends the session on the
  // server (every token issued before now stops working), clears every
  // stored token here, clears the editor's session on my.easypeazy.site, and
  // leaves for /login immediately — never waits on a re-render.
  const signOutEverywhere = useCallback(async (event) => {
    // A click event may arrive here; never let it reach a form or link.
    try { if (event && typeof event.preventDefault === 'function') event.preventDefault(); } catch (e) { /* ignore */ }
    const withTimeout = (p, ms) => Promise.race([
      Promise.resolve(p).catch(() => undefined),
      new Promise((r) => setTimeout(r, ms)),
    ]);
    const stored = (() => { try { return localStorage.getItem('ew_token'); } catch (e) { return null; } })();
    try {
      if (stored) {
        await withTimeout(axios.post(apiUrl('/auth/logout'), {}, {
          headers: { Authorization: `Bearer ${stored}` }, timeout: 3000, withCredentials: true,
        }), 3500);
      }
    } catch (e) {
      /* signing out locally still happens below */
    } finally {
      try {
        Object.keys(localStorage).filter((k) => /^(ew_|ep_)/.test(k) || k === 'token').forEach((k) => localStorage.removeItem(k));
        sessionStorage.clear();
      } catch (e) { /* storage blocked */ }
      try { logout(); } catch (e) { /* state already gone */ }
      try {
        const host = typeof window !== 'undefined' ? window.location.hostname : '';
        if (/(^|\.)easypeazy\.site$/.test(host) && typeof fetch === 'function') {
          await withTimeout(fetch('https://my.easypeazy.site/api/auth/signout', {
            method: 'GET', mode: 'no-cors', credentials: 'include', keepalive: true,
          }), 1500);
        }
      } catch (e) {
        /* editor session clears on its own expiry */
      } finally {
        // Always leave, whatever the network did.
        window.location.replace('/login');
      }
    }
  }, [logout]);

  // Restore once on mount. Do not re-fetch /auth/me when login() sets token —
  // that is what bounced leftover Sign In: leftover login 200, then a failed
  // or stale GET /auth/me called logout() and remounted /login with no error.
  useEffect(() => {
    const stored = localStorage.getItem('ew_token');
    if (!stored) {
      setLoading(false);
      return undefined;
    }
    const gen = ++restoreGen.current;
    setAuthHeader(stored);
    axios.get(apiUrl('/auth/me'))
      .then((res) => {
        if (shouldIgnoreRestore(restoreGen.current, gen)) return;
        setToken(stored);
        setUser(res.data);
        setSessionMessage('');
      })
      .catch((err) => {
        if (shouldIgnoreRestore(restoreGen.current, gen)) return;
        logout(restoreErrorMessage(err));
      })
      .finally(() => {
        if (!shouldIgnoreRestore(restoreGen.current, gen)) setLoading(false);
      });
    return undefined;
  }, [setAuthHeader, setSessionMessage, logout]);

  const login = async (email, password) => {
    restoreGen.current += 1;
    setSessionMessage('');
    try {
      const res = await axios.post(apiUrl('/auth/login'), { email, password });
      localStorage.setItem('ew_token', res.data.token);
      setToken(res.data.token);
      setAuthHeader(res.data.token);
      setUser(res.data.user);
      setLoading(false);
      return res.data;
    } catch (err) {
      setSessionMessage(loginErrorMessage(err));
      throw err;
    }
  };

  // Sign in from a sibling app's signed handoff. Same session shape as a normal
  // login, so everything downstream is unaware of how the person arrived.
  const ssoLogin = async (handoffToken) => {
    restoreGen.current += 1;
    setSessionMessage('');
    try {
      const res = await axios.post(apiUrl('/auth/sso'), { token: handoffToken });
      localStorage.setItem('ew_token', res.data.token);
      setToken(res.data.token);
      setAuthHeader(res.data.token);
      setUser(res.data.user);
      setLoading(false);
      return res.data;
    } catch (err) {
      setSessionMessage(loginErrorMessage(err));
      throw err;
    }
  };

  const signup = async (data) => {
    restoreGen.current += 1;
    setSessionMessage('');
    try {
      // United Under God customers sign up on easypeazy.unitedundergod.org and
      // are offered every shared web address, so record that door.
      const host = (typeof window !== 'undefined' && window.location.hostname) || '';
      const door = host.endsWith('unitedundergod.org') ? { source: 'unitedundergod' } : {};
      const res = await axios.post(apiUrl('/auth/signup'), { ...data, ...door });
      localStorage.setItem('ew_token', res.data.token);
      setToken(res.data.token);
      setAuthHeader(res.data.token);
      setUser(res.data.user);
      setLoading(false);
      return res.data;
    } catch (err) {
      setSessionMessage(loginErrorMessage(err));
      throw err;
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, sessionError, login, ssoLogin, signup, logout, signOutEverywhere }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
